import React, { useState, useEffect } from 'react';
import { useOrderModal } from '../contexts/OrderModalContext';

import Navigation from './Navigation';
import Footer from './Footer';
import LocationPageContent from './LocationPageContent';
import SchemaMarkup from './SchemaMarkup'; // Import the schema component

// Updated LocationsMartinezView component
const LocationsMartinezView = ({ locationName = "Martinez" }) => {
  const [locationData, setLocationData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // const { openModal } = useOrderModal();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchLocationData = async () => {
      try {
        setLoading(true);
        // Using the API endpoint from your router
        const response = await fetch(`/api/locations/${locationName}`);
        const result = await response.json();
        
        if (result.success) {
          setLocationData(result.data);
        } else {
          setError(result.message || 'Failed to load location data');
        }
      } catch (err) {
        setError('Failed to fetch location data');
        console.error('Error fetching location:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchLocationData();
  }, [locationName]);

  // Update meta tags when location data changes
  useEffect(() => {
    if (!locationData) return;

    const updateMetaTags = () => {
      const title = `${locationData.name} | Kinder's Meats Deli BBQ & Catering`;
      const description = `Visit Kinder's Meats Deli BBQ & Catering in ${locationData.address.city}, ${locationData.address.state}. Order pickup, delivery, or catering. Call ${locationData.phoneNumber}. Address: ${locationData.address.address1}, ${locationData.address.city}.`;
      const canonical = `https://kindersmeats.com/locations/${locationName.toLowerCase()}`;
      const imageUrl = locationData.photoSrc ? 
        `https://kindersmeats.com/images/locations/${locationData.photoSrc}` : 
        'https://kindersmeats.com/images/locations/kinders-martinez.jpg';

      // Update document title
      document.title = title;

      // Helper function to update or create meta tags
      const updateMetaTag = (name, content, property = false) => {
        const attribute = property ? 'property' : 'name';
        let meta = document.querySelector(`meta[${attribute}="${name}"]`);
        
        if (!meta) {
          meta = document.createElement('meta');
          meta.setAttribute(attribute, name);
          document.head.appendChild(meta);
        }
        
        meta.setAttribute('content', content);
      };

      // Helper function to update or create link tags
      const updateLinkTag = (rel, href) => {
        let link = document.querySelector(`link[rel="${rel}"]`);
        
        if (!link) {
          link = document.createElement('link');
          link.setAttribute('rel', rel);
          document.head.appendChild(link);
        }
        
        link.setAttribute('href', href);
      };

      // Basic Meta Tags
      updateMetaTag('description', description);
      updateMetaTag('keywords', `Kinder's Meats, BBQ, ${locationData.address.city}, ${locationData.address.state}, restaurant, catering, deli, pickup, delivery`);
      updateMetaTag('robots', 'index, follow');
      updateMetaTag('author', 'Kinder\'s Meats Deli BBQ & Catering');
      updateLinkTag('canonical', canonical);

      // Open Graph Meta Tags
      updateMetaTag('og:title', title, true);
      updateMetaTag('og:description', description, true);
      updateMetaTag('og:image', imageUrl, true);
      updateMetaTag('og:url', canonical, true);
      updateMetaTag('og:type', 'business.business', true);
      updateMetaTag('og:site_name', 'Kinder\'s Meats Deli BBQ & Catering', true);
      updateMetaTag('business:contact_data:street_address', `${locationData.address.address1} ${locationData.address.address2 || ''}`, true);
      updateMetaTag('business:contact_data:locality', locationData.address.city, true);
      updateMetaTag('business:contact_data:region', locationData.address.state, true);
      updateMetaTag('business:contact_data:postal_code', locationData.address.zip, true);
      updateMetaTag('business:contact_data:country_name', 'United States', true);
      updateMetaTag('business:contact_data:phone_number', locationData.phoneNumber, true);

      // Twitter Card Meta Tags
      updateMetaTag('twitter:card', 'summary_large_image');
      updateMetaTag('twitter:title', title);
      updateMetaTag('twitter:description', description);
      updateMetaTag('twitter:image', imageUrl);

      // Local Business Meta Tags
      updateMetaTag('geo.placename', `${locationData.address.city}, ${locationData.address.state}`);
      updateMetaTag('geo.region', locationData.address.state);
      
      if (locationData.coordinates) {
        updateMetaTag('ICBM', `${locationData.coordinates.lat}, ${locationData.coordinates.lng}`);
        updateMetaTag('geo.position', `${locationData.coordinates.lat};${locationData.coordinates.lng}`);
      }
    };

    updateMetaTags();

    // Cleanup function to reset title when component unmounts
    return () => {
      document.title = 'Kinder\'s Meats Deli BBQ & Catering';
    };
  }, [locationData, locationName]);

  if (loading) {
    return (
      <section className="theme-light m-0 p-0" data-theme="light">
        <div className="is-flex is-flex-direction-column fullHeight is-justify-content-center is-align-items-center">
          <p>Loading location details...</p>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="theme-light m-0 p-0" data-theme="light">
        <div className="is-flex is-flex-direction-column fullHeight is-justify-content-center is-align-items-center">
          <p className="has-text-danger">Error: {error}</p>
        </div>
      </section>
    );
  }

  return (
    <section className="theme-light m-0 p-0" data-theme="light">
      {/* Schema Markup */}
      <SchemaMarkup locationData={locationData} />

      <div className='is-flex is-flex-direction-column fullHeight is-justify-content-space-between'>
        <div className='is-flex is-flex-direction-column is-justify-content-start'>
          <Navigation />

          <LocationPageContent 
            className="content-space-160 is-10-mobile is-10-tablet is-10-desktop is-8-widescreen is-8-fullhd is-offset-1-mobile is-offset-1-tablet is-offset-1-desktop is-offset-2-widescreen is-offset-2-fullhd"
            locationData={locationData}
          />
        </div>   
        
        <Footer />
      </div>
    </section>
  );
};

export default LocationsMartinezView;